/**
 * <aspilon-video-feed> — focus carousel for social videos.
 *
 * Slides share one grid cell and are shifted into slots via `--offset`
 * (0 centre, ±1 neighbours, further = hidden). Only the centred video plays;
 * everything else stays paused at preload="none" behind its poster.
 *
 * Attributes:
 *   advance-on-end   move to the next video when the centred one ends
 *                    (otherwise the centred video loops)
 *
 * Videos are always muted. Pauses when the section leaves the viewport or the
 * tab is hidden. Under prefers-reduced-motion nothing autoplays: tapping the
 * centred card plays/pauses it.
 */
class AspilonVideoFeed extends HTMLElement {
  #index = 0;
  #settle = null;
  #inView = false;
  #dragX = null;
  #suppressClick = false;
  #observer = null;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.aspilon-video-feed__slide'));
    this.dots = Array.from(this.querySelectorAll('.aspilon-video-feed__dot'));
    this.n = this.slides.length;
    if (!this.n) return;

    this.advanceOnEnd = this.hasAttribute('advance-on-end');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelector('[ref="prev"]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[ref="next"]')?.addEventListener('click', () => this.#step(1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.#go(Number(dot.dataset.index))));

    this.slides.forEach((slide, i) => {
      slide.addEventListener('click', (e) => {
        if (this.#suppressClick) { e.preventDefault(); return; }
        if (i !== this.#index) { e.preventDefault(); this.#go(i); return; }
        if (e.target.closest('a')) return; // product tag, Shop link or the "View on TikTok" link
        // Centred video linked to a product: open it.
        const productUrl = slide.querySelector('.aspilon-video__frame')?.dataset.productUrl;
        if (productUrl) { e.preventDefault(); window.location.assign(productUrl); return; }
        // Reduced motion: nothing autoplays, so a tap on an unlinked centred card plays/pauses it.
        if (this.reducedMotion) { e.preventDefault(); this.#togglePlay(); }
      });
      const video = slide.querySelector('video');
      video?.addEventListener('ended', () => {
        if (i === this.#index && this.advanceOnEnd) this.#step(1);
      });
    });

    // Swipe (pointer events cover touch and mouse drag).
    this.addEventListener('pointerdown', (e) => { this.#dragX = e.clientX; });
    this.addEventListener('pointerup', (e) => {
      if (this.#dragX === null) return;
      const dx = e.clientX - this.#dragX;
      this.#dragX = null;
      if (Math.abs(dx) < 40) return;
      this.#suppressClick = true;
      setTimeout(() => (this.#suppressClick = false), 350);
      this.#step(dx < 0 ? 1 : -1);
    });
    this.addEventListener('pointercancel', () => { this.#dragX = null; });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.#step(-1);
      if (e.key === 'ArrowRight') this.#step(1);
    });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#inView = entry.isIntersecting;
      this.#syncPlayback();
    }, { threshold: 0.25 });
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
    }

    this.#layout(true);
  }

  disconnectedCallback() {
    clearTimeout(this.#settle);
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.slides.forEach((s) => s.querySelector('video')?.pause());
  }

  get #activeVideo() {
    return this.slides[this.#index]?.querySelector('video') || null;
  }

  get #transitionMs() {
    if (this.reducedMotion) return 0;
    const d = parseFloat(getComputedStyle(this.slides[0]).transitionDuration) || 0.6;
    return d * 1000;
  }

  #step(delta) {
    this.#go((this.#index + delta + this.n) % this.n);
  }

  /** Signed circular distance from the active slide, in (-n/2, n/2]. */
  #offsetOf(i) {
    let d = (i - this.#index) % this.n;
    if (d > this.n / 2) d -= this.n;
    if (d <= -this.n / 2) d += this.n;
    return d;
  }

  #go(index) {
    if (index === this.#index || !this.slides[index]) return;
    this.#index = index;
    this.classList.add('is-moving');
    this.#layout(false);
    clearTimeout(this.#settle);
    this.#settle = setTimeout(() => this.classList.remove('is-moving'), this.#transitionMs + 30);
  }

  /**
   * Put every slide in its slot. A slide whose slot changes by more than one
   * position (the wrap) is teleported while invisible so it never flies across.
   */
  #layout(instant) {
    const teleport = [];
    this.slides.forEach((slide, i) => {
      const next = this.#offsetOf(i);
      const prev = Number(slide.style.getPropertyValue('--offset') || next);
      const active = next === 0;

      if (instant || Math.abs(next - prev) > 1) {
        slide.style.transition = 'none';
        slide.classList.add('is-hidden');
        teleport.push(slide);
      }
      slide.style.setProperty('--offset', next);
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-outer', Math.abs(next) === 2);
      slide.classList.toggle('is-far', Math.abs(next) > 2);
      slide.style.zIndex = String(this.n - Math.abs(next));
      slide.setAttribute('aria-hidden', String(!active));
      // Neighbours stay clickable (click = bring to centre) but not tabbable.
      slide.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', active ? '0' : '-1'));
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === this.#index)));

    if (teleport.length) {
      void this.offsetWidth; // commit the un-transitioned positions
      const reveal = () => teleport.forEach((s) => {
        s.style.transition = '';
        s.classList.remove('is-hidden');
      });
      requestAnimationFrame(reveal);
      setTimeout(reveal, 60); // rAF doesn't fire in hidden tabs
    }

    this.#syncPlayback();
  }

  /** Play the centred video (if allowed), pause and rewind every other one. */
  #syncPlayback() {
    const shouldPlay = this.#inView && !document.hidden && !this.reducedMotion;
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.#index) {
        video.loop = !this.advanceOnEnd;
        video.muted = true;
        if (shouldPlay) video.play().catch(() => {});
        else if (!this.reducedMotion) video.pause();
      } else {
        if (!video.paused) video.pause();
        if (video.currentTime > 0) video.currentTime = 0;
      }
    });
  }

  #togglePlay() {
    const video = this.#activeVideo;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  #onVisibility = () => this.#syncPlayback();

  #onBlockSelect = (e) => {
    const i = this.slides.findIndex((s) => s.dataset.blockId === e.detail.blockId || s.querySelector(`[data-block-id="${e.detail.blockId}"]`));
    if (i !== -1) this.#go(i);
  };
}

if (!customElements.get('aspilon-video-feed')) {
  customElements.define('aspilon-video-feed', AspilonVideoFeed);
}
